import app from 'flarum/forum/app';
import Component from 'flarum/common/Component';
import extractText from 'flarum/common/utils/extractText';
import Avatar from 'ext:ernestdefoe/parley/forum/components/Avatar';
import Icons from '../icons';
import VoicePill from './VoicePill';
import deviceAlert from '../deviceAlert';
const t = (key, params) => app.translator.trans('ernestdefoe-parley-calls.forum.' + key, params);

/**
 * Start an element, and if the browser holds the sound back until the page is
 * touched, start it on the next touch.
 */
const play = el => el.play?.().catch(() => document.addEventListener('pointerdown', () => el.isConnected && el.paused && el.play().catch(() => {}), {
  once: true
}));

/** Bind a stream to a <video> or <audio> element once, and keep it bound. */
const attach = (stream, muted) => ({
  oncreate: v => {
    v.dom.srcObject = stream;
    v.dom.muted = !!muted;
    play(v.dom);
  },
  onupdate: v => {
    if (v.dom.srcObject !== stream) {
      v.dom.srcObject = stream;
      play(v.dom);
    }
  }
});

/**
 * The call, drawn the mockup's way: the full screen with the other person
 * large and you in the corner, the small card you keep while browsing, and the
 * card that rings.
 */
export default class CallLayer extends Component {
  oncreate(vnode) {
    super.oncreate(vnode);
    // The clock in the corner moves once a second; nothing else needs to.
    this.tick = setInterval(() => app.parleyCalls.session && app.parleyCalls.session.phase === 'live' && m.redraw(), 1000);
  }
  onremove(vnode) {
    super.onremove(vnode);
    clearInterval(this.tick);
  }
  view() {
    const s = app.parleyCalls.session;
    const v = app.parleyVoice;
    // In a room's voice chat with that room's window not in view: the pill.
    const voicePill = v && v.session && !app.parley.isVisible(v.session.roomId) && !v.session.room.onScreen ? m(VoicePill, null) : null;
    if (!s) return m("div", {
      className: "Parley ParleyCalls"
    }, voicePill);
    return m("div", {
      className: "Parley ParleyCalls"
    }, s.remoteAudio ? m("audio", Object.assign({
      autoplay: true
    }, attach(s.remoteAudio, false))) : null, s.phase === 'incoming' ? this.toast(s) : s.pip ? this.pip(s) : this.screen(s));
  }
  toast(s) {
    const c = app.parleyCalls;
    const peer = s.peer || {};
    return m("div", {
      className: "pl-toast",
      role: "alertdialog",
      "aria-label": extractText(t('incoming_label', {
        name: peer.displayName
      }))
    }, Avatar(peer, 44), m("div", {
      className: "pl-who"
    }, m("b", null, peer.displayName), m("span", null, t(s.kind === 'video' ? 'incoming_video' : 'incoming_voice'))), m("button", {
      className: "pl-cb end",
      "aria-label": extractText(t('decline')),
      title: extractText(t('decline')),
      onclick: () => c.decline()
    }, Icons.end()), m("button", {
      className: "pl-cb go",
      "aria-label": extractText(t('accept')),
      title: extractText(t('accept')),
      onclick: () => c.accept()
    }, s.kind === 'video' ? Icons.video() : Icons.phone()));
  }
  screen(s) {
    const peer = s.peer || {};
    const remoteVideo = s.remoteStream.getVideoTracks().some(tr => tr.readyState === 'live' && !tr.muted) && (s.remote.cam || s.remote.sharing);
    const waiting = s.phase === 'outgoing' || s.phase === 'connecting' && !s.startedAt;
    const status = s.phase === 'outgoing' ? t('ringing') : s.reconnecting ? t('reconnecting') : waiting ? t('connecting') : null;
    return m("div", {
      className: "pl-call",
      role: "dialog",
      "aria-label": extractText(t('call_with', {
        name: peer.displayName
      }))
    }, m("div", {
      className: "pl-call-top"
    }, m("span", {
      className: "pl-enc"
    }, s.relayed ? Icons.relay() : Icons.lock(), " ", s.relayed ? t('relayed') : t('direct')), m("span", {
      className: "pl-t"
    }, s.elapsed() || status || '')), m("div", {
      className: 'pl-tile' + (remoteVideo ? ' cam' : '')
    }, m("video", Object.assign({
      className: 'pl-remote' + (remoteVideo ? '' : ' hidden') + (s.remote.sharing ? ' screen' : ''),
      autoplay: true,
      playsinline: true
    }, attach(s.remoteStream, true))), waiting ? m("div", {
      className: "pl-ring"
    }, Avatar(peer, 110), m("h3", null, peer.displayName), m("p", null, status)) : !remoteVideo ? Avatar(peer, 120, {
      className: 'pl-big' + (s.speaking ? ' speaking' : '')
    }) : null, !waiting ? m("span", {
      className: "pl-name"
    }, s.remote.muted ? Icons.micoff(14) : null, peer.displayName, s.remote.sharing ? m("em", null, t('sharing_screen')) : null) : null), this.selfie(s), m("div", {
      className: "pl-ctrl"
    }, this.controls(s)));
  }
  selfie(s) {
    const showVideo = s.sharing ? !!s.screenTrack : s.camOn && !!s.camTrack;
    // A stream of just the track being shown, so the corner shows the screen
    // while sharing and the camera otherwise.
    const track = s.sharing ? s.screenTrack : s.camTrack;
    if (!this.selfStream || this.selfTrack !== track) {
      this.selfTrack = track;
      this.selfStream = track ? new MediaStream([track]) : null;
    }
    if (s.kind !== 'video' && !s.camOn && !s.sharing) return null;
    return m("div", {
      className: "pl-selfie"
    }, showVideo && this.selfStream ? m("video", Object.assign({
      className: s.sharing ? '' : 'mirror',
      autoplay: true,
      playsinline: true
    }, attach(this.selfStream, true))) : null, s.sharing ? m("span", {
      className: "pl-off"
    }, t('you_are_sharing')) : !s.camOn ? m("span", {
      className: "pl-off"
    }, t('camera_off')) : null);
  }
  controls(s) {
    const c = app.parleyCalls;
    const live = s.phase !== 'outgoing';
    const wrap = fn => () => fn().catch?.(() => deviceAlert('device_failed'));
    return [m("button", {
      className: 'pl-cb' + (s.muted ? ' on' : ''),
      "aria-pressed": String(s.muted),
      "aria-label": extractText(t(s.muted ? 'unmute' : 'mute')),
      title: extractText(t(s.muted ? 'unmute' : 'mute')),
      onclick: () => s.toggleMute()
    }, s.muted ? Icons.micoff() : Icons.mic()), m("button", {
      className: 'pl-cb' + (!s.camOn ? ' on' : ''),
      disabled: !live,
      "aria-pressed": String(!s.camOn),
      "aria-label": extractText(t(s.camOn ? 'camera_off_action' : 'camera_on_action')),
      title: extractText(t(s.camOn ? 'camera_off_action' : 'camera_on_action')),
      onclick: wrap(() => s.toggleCamera())
    }, Icons.video(20)), c.canShare() ? m("button", {
      className: 'pl-cb' + (s.sharing ? ' on' : ''),
      disabled: !live,
      "aria-pressed": String(s.sharing),
      "aria-label": extractText(t('share')),
      title: extractText(t(s.sharing ? 'stop_sharing' : 'share')),
      onclick: wrap(() => s.toggleShare())
    }, Icons.share()) : null, m("button", {
      className: "pl-cb",
      "aria-label": extractText(t('keep_browsing')),
      title: extractText(t('keep_browsing')),
      onclick: () => s.pip = true
    }, Icons.pip()), m("button", {
      className: "pl-cb end",
      "aria-label": extractText(t('end_call')),
      title: extractText(t('end_call')),
      onclick: () => c.hangUp()
    }, Icons.end())];
  }
  pip(s) {
    const c = app.parleyCalls;
    const peer = s.peer || {};
    const remoteVideo = s.remoteStream.getVideoTracks().some(tr => tr.readyState === 'live' && !tr.muted) && (s.remote.cam || s.remote.sharing);
    return m("div", {
      className: "pl-pip",
      role: "dialog",
      "aria-label": extractText(t('call_with', {
        name: peer.displayName
      }))
    }, m("div", {
      className: "pl-pv"
    }, m("video", Object.assign({
      className: 'pl-remote' + (remoteVideo ? '' : ' hidden'),
      autoplay: true,
      playsinline: true
    }, attach(s.remoteStream, true))), !remoteVideo ? Avatar(peer, 56, {
      className: s.speaking ? 'speaking' : ''
    }) : null), m("div", {
      className: "pl-pb"
    }, m("span", null, m("b", null, peer.displayName), m("br", null), m("span", {
      className: "pl-t"
    }, s.elapsed() || (s.phase === 'outgoing' ? t('ringing') : t('connecting')))), m("span", {
      className: "pl-pb-ctrl"
    }, m("button", {
      className: 'pl-cb' + (s.muted ? ' on' : ''),
      "aria-label": extractText(t(s.muted ? 'unmute' : 'mute')),
      onclick: () => s.toggleMute()
    }, s.muted ? Icons.micoff() : Icons.mic()), m("button", {
      className: "pl-cb",
      "aria-label": extractText(t('back_to_call')),
      title: extractText(t('back_to_call')),
      onclick: () => s.pip = false
    }, Icons.expand()), m("button", {
      className: "pl-cb end",
      "aria-label": extractText(t('end_call')),
      onclick: () => c.hangUp()
    }, Icons.end()))));
  }
}
flarum.reg.add('ernestdefoe-parley-calls', 'forum/components/CallLayer', CallLayer);